/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import type { Steps } from '@/types';

/**
 * Position (0-based) d'une étape dans le parcours ORDONNÉ d'un type d'acte
 * (`STEPS_BY_TYPE_ACTE[type]`), ou `-1` si l'étape n'appartient pas à ce
 * parcours.
 *
 * Toute la navigation de l'assistant doit passer par cette position, jamais
 * par la valeur numérique brute de l'enum `Steps` : deux types d'acte
 * peuvent avoir des parcours différents (ex. reconnaissance a une étape
 * `Enfant` que la naissance n'a pas), donc les valeurs de `Steps` ne sont pas
 * forcément consécutives — ni dans le même ORDRE relatif de progression —
 * pour un type donné.
 */
export const getStepPosition = (orderedSteps: Steps[], step: Steps): number =>
  orderedSteps.indexOf(step);

/**
 * Étapes à valider pour aller de `fromStep` (inclus) à `toStep` (exclu),
 * dans l'ordre du parcours. Sans `toStep`, valide jusqu'à la fin du parcours
 * (cf. `Assistant.handleFinish`, qui valide tout avant l'envoi).
 */
export const getValidationRange = (
  orderedSteps: Steps[],
  fromStep: Steps,
  toStep?: Steps,
): Steps[] => {
  const fromPosition = getStepPosition(orderedSteps, fromStep);
  const toPosition =
    toStep !== undefined
      ? getStepPosition(orderedSteps, toStep)
      : orderedSteps.length;

  return orderedSteps.slice(fromPosition, toPosition);
};

export interface StepNavigation {
  /** Position de `currentStep` dans `orderedSteps` (`-1` si absent du parcours). */
  position: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  /** Étape précédente, ou `currentStep` si déjà sur la première (pas de recul possible). */
  previousStep: Steps;
  /** Étape suivante, ou `currentStep` si déjà sur la dernière (pas d'avance possible). */
  nextStep: Steps;
}

/**
 * Dérive tout ce dont le header (numérotation, style des badges) et le
 * footer (boutons précédent/suivant/terminer) ont besoin pour une étape
 * courante donnée, à partir de sa POSITION dans le parcours du type d'acte.
 */
export const getStepNavigation = (
  orderedSteps: Steps[],
  currentStep: Steps,
): StepNavigation => {
  const position = getStepPosition(orderedSteps, currentStep);

  return {
    position,
    isFirstStep: position <= 0,
    isLastStep: position === orderedSteps.length - 1,
    previousStep: position > 0 ? orderedSteps[position - 1] : currentStep,
    nextStep:
      position >= 0 && position < orderedSteps.length - 1
        ? orderedSteps[position + 1]
        : currentStep,
  };
};
